import React, { useState, useMemo, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { statisticsService } from '../../services/statisticsService';
import { DailyActivity, Quest } from '../../types';
import WeekView from '../calendar/WeekView';
import DayView from '../calendar/DayView';
import QuestEditModal from '../QuestEditModal';
import CreateQuestModal from '../CreateQuestModal';
import { trackEvent, AnalyticsEvent } from '../../services/analyticsService';

type CalendarView = 'month' | 'week' | 'day';

const CalendarPage: React.FC = () => {
  const { quests, programs, profile, updateQuest, deleteQuest, completeQuest, createQuest } = useUser();
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [view, setView] = useState<CalendarView>('month');
  const [selectedQuest, setSelectedQuest] = useState<Quest | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [prefilledDate, setPrefilledDate] = useState<string | undefined>(undefined);
  const [prefilledTime, setPrefilledTime] = useState<string | undefined>(undefined);

  // Track calendar page view on mount and when view changes
  useEffect(() => {
    trackEvent(AnalyticsEvent.CALENDAR_VIEWED, { view });
  }, [view]);

  // Get calendar data
  const calendarData = useMemo(() => {
    if (!profile) return [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days: Array<{
      date: string;
      day: number;
      isCurrentMonth: boolean;
      activity: DailyActivity | null;
      completedQuests: Quest[];
    }> = [];

    // Previous month's days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        date: dateStr,
        day,
        isCurrentMonth: false,
        activity: null,
        completedQuests: [],
      });
    }

    // Current month's days
    const allStats = statisticsService.getStatistics();
    const todayStr = new Date().toISOString().split('T')[0];

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const activity = allStats.find(s => s.date === dateStr) || null;
      const isToday = dateStr === todayStr;
      
      // Get all quests for this day categorized by status
      const dayQuests = quests.filter(q => {
        // Completed on this day
        if (q.status === 'completed' && q.completedAt) {
          const questDate = new Date(q.completedAt);
          return questDate.getFullYear() === year &&
                 questDate.getMonth() === month &&
                 questDate.getDate() === day;
        }
        
        // Scheduled for this day
        if (q.scheduledFor === dateStr) {
          return true;
        }
        
        // Active quests (no schedule) show on TODAY
        if (isToday && q.status === 'active' && !q.scheduledFor) {
          return true;
        }
        
        return false;
      });

      days.push({
        date: dateStr,
        day,
        isCurrentMonth: true,
        activity,
        completedQuests: dayQuests,
      });
    }

    // Next month's days to fill the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const dateStr = `${year}-${String(month + 2).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        date: dateStr,
        day,
        isCurrentMonth: false,
        activity: null,
        completedQuests: [],
      });
    }

    return days;
  }, [currentDate, quests]);

  // Get intensity level for heat map
  const getIntensityLevel = (activity: DailyActivity | null): number => {
    if (!activity || activity.questsCompleted === 0) return 0;
    if (activity.questsCompleted <= 2) return 1;
    if (activity.questsCompleted <= 4) return 2;
    if (activity.questsCompleted <= 6) return 3;
    return 4;
  };

  const getIntensityColor = (level: number): string => {
    const colors = [
      'bg-system-darker border-gray-700',
      'bg-system-blue/20 border-system-blue/30',
      'bg-system-blue/40 border-system-blue/50',
      'bg-system-blue/60 border-system-blue/70',
      'bg-system-blue border-system-blue shadow-neon-blue',
    ];
    return colors[level];
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
    setSelectedDay(null);
  };

  // Get translated month name
  const getMonthName = (): string => {
    const monthIndex = currentDate.getMonth();
    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'] as const;
    const monthKey = monthKeys[monthIndex];
    return `${t.calendar.monthsFull[monthKey]} ${currentDate.getFullYear()}`;
  };
  const monthName = getMonthName();
  const weekDays = [t.calendar.weekDays.sun, t.calendar.weekDays.mon, t.calendar.weekDays.tue, t.calendar.weekDays.wed, t.calendar.weekDays.thu, t.calendar.weekDays.fri, t.calendar.weekDays.sat];
  
  if (!profile) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-400">{t.common.loading}</div>
      </div>
    );
  }


  // Check for time conflicts
  const checkTimeConflict = (date: string, startTime: string, endTime: string, excludeQuestId?: string): Quest[] => {
    const conflictingQuests = quests.filter(q => {
      if (q.id === excludeQuestId) return false;
      if (q.scheduledFor !== date) return false;
      if (!q.scheduledTime) return false;
      
      // Check if times overlap
      const qStart = q.scheduledTime.start;
      const qEnd = q.scheduledTime.end;
      
      // Two time ranges overlap if: start1 < end2 AND start2 < end1
      return startTime < qEnd && qStart < endTime;
    });
    
    return conflictingQuests;
  };

  // Handlers for new views
  const handleQuestClick = (quest: Quest) => {
    setSelectedQuest(quest);
  };

  const handleTimeSlotClick = (date: string, time: string) => {
    setPrefilledDate(date);
    setPrefilledTime(time);
    setShowCreateModal(true);
  };

  const handleQuestDragEnd = (questId: string, newDate: string, newStartTime: string) => {
    const quest = quests.find(q => q.id === questId);
    if (!quest) return;
    
    // Calculate end time based on duration
    let newEndTime = newStartTime;
    if (quest.scheduledTime) {
      const [oldStartHour, oldStartMin] = quest.scheduledTime.start.split(':').map(Number);
      const [oldEndHour, oldEndMin] = quest.scheduledTime.end.split(':').map(Number);
      const durationMinutes = (oldEndHour * 60 + oldEndMin) - (oldStartHour * 60 + oldStartMin);
      
      const [newStartHour, newStartMin] = newStartTime.split(':').map(Number);
      const newEndMinutes = newStartHour * 60 + newStartMin + durationMinutes;
      const newEndHour = Math.floor(newEndMinutes / 60);
      const newEndMin = newEndMinutes % 60;
      newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(newEndMin).padStart(2, '0')}`;
    } else {
      // Default 1-hour duration for quests without time
      const [startHour, startMin] = newStartTime.split(':').map(Number);
      const endMinutes = startHour * 60 + startMin + 60;
      const endHour = Math.floor(endMinutes / 60);
      const endMin = endMinutes % 60;
      newEndTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
    }
    
    // Check for conflicts
    const conflicts = checkTimeConflict(newDate, newStartTime, newEndTime, questId);
    if (conflicts.length > 0) {
      // Show warning but still allow the update
      console.warn(`⚠️ Time conflict detected with: ${conflicts.map(q => q.title).join(', ')}`);
      // TODO: Could show a System-style warning notification
    }
    
    // Update quest with new schedule (silent mode to avoid notification spam during drag)
    updateQuest(questId, {
      scheduledFor: newDate,
      scheduledTime: {
        start: newStartTime,
        end: newEndTime
      },
      allDay: false
    }, true); // silent = true
  };

  const handleQuestDragEndDay = (questId: string, newStartTime: string) => {
    if (!selectedDay) return;
    handleQuestDragEnd(questId, selectedDay, newStartTime);
  };

  const handleQuestResize = (questId: string, newStartTime: string, newEndTime: string) => {
    const quest = quests.find(q => q.id === questId);
    if (!quest || !quest.scheduledFor) return;

    // Check for conflicts
    const conflicts = checkTimeConflict(quest.scheduledFor, newStartTime, newEndTime, questId);
    if (conflicts.length > 0) {
      console.warn(`⚠️ Time conflict detected with: ${conflicts.map(q => q.title).join(', ')}`);
    }

    // Update quest with new times (silent mode to avoid notification spam)
    updateQuest(questId, {
      scheduledTime: {
        start: newStartTime,
        end: newEndTime
      }
    }, true); // silent = true
  };

  return (
    <div className="animate-fade-in max-w-7xl mx-auto w-full p-3 sm:p-6">
      {/* Header */}
      <div className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 mb-6"
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
            <span className="font-orbitron text-lg sm:text-2xl tracking-wider text-system-blue uppercase">
              ［ {t.calendar.title} ］
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
            {t.calendar.subtitle}
          </p>
        </div>
        
        {/* View Switcher */}
        <div className="flex gap-1 sm:gap-2 bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-1 w-full sm:w-auto"
          style={{
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
          }}
        >
          <button
            onClick={() => setView('month')}
            className={`
              flex-1 sm:flex-initial px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl font-orbitron font-bold text-xs sm:text-sm uppercase tracking-wider transition-all relative border-t-2 border-l-2 border-r-2 border-b-2
              ${view === 'month' 
                ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-blue-500/50 border-r-blue-700/70 border-b-blue-700/70 text-white scale-105' 
                : 'bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:text-white hover:border-blue-500/50 hover:scale-105'
              }
            `}
            style={view === 'month' ? {
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(100, 150, 255, 0.15)'
            } : {
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            <span className="hidden sm:inline relative z-10">📅 {t.calendar.month}</span>
            <span className="sm:hidden relative z-10">📅</span>
            {view === 'month' && (
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl pointer-events-none"></div>
            )}
          </button>
          <button
            onClick={() => setView('week')}
            className={`
              flex-1 sm:flex-initial px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl font-orbitron font-bold text-xs sm:text-sm uppercase tracking-wider transition-all relative border-t-2 border-l-2 border-r-2 border-b-2
              ${view === 'week' 
                ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-blue-500/50 border-r-blue-700/70 border-b-blue-700/70 text-white scale-105' 
                : 'bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:text-white hover:border-blue-500/50 hover:scale-105'
              }
            `}
            style={view === 'week' ? {
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(100, 150, 255, 0.15)'
            } : {
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            <span className="hidden sm:inline relative z-10">📊 {t.calendar.week}</span>
            <span className="sm:hidden relative z-10">📊</span>
            {view === 'week' && (
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl pointer-events-none"></div>
            )}
          </button>
          <button
            onClick={() => {
              setView('day');
              if (!selectedDay) {
                setSelectedDay(new Date().toISOString().split('T')[0]);
              }
            }}
            className={`
              flex-1 sm:flex-initial px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl font-orbitron font-bold text-xs sm:text-sm uppercase tracking-wider transition-all relative border-t-2 border-l-2 border-r-2 border-b-2
              ${view === 'day' 
                ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-blue-500/50 border-r-blue-700/70 border-b-blue-700/70 text-white scale-105' 
                : 'bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:text-white hover:border-blue-500/50 hover:scale-105'
              }
            `}
            style={view === 'day' ? {
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(100, 150, 255, 0.15)'
            } : {
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            <span className="hidden sm:inline relative z-10">📍 {t.calendar.day}</span>
            <span className="sm:hidden relative z-10">📍</span>
            {view === 'day' && (
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl pointer-events-none"></div>
            )}
          </button>
        </div>
        </div>
      </div>

      {/* Streak Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 hover:border-blue-500/60 transition-all hover:shadow-lg hover:shadow-blue-500/20"
          style={{
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
          }}
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl sm:text-4xl animate-pulse">🔥</div>
            <div>
              <div className="text-xs text-gray-400 font-rajdhani uppercase tracking-wide">{t.calendar.currentStreak}</div>
              <div className="text-2xl sm:text-3xl font-black font-orbitron text-system-blue">{profile.streak} {t.calendar.days}</div>
            </div>
          </div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 hover:border-purple-500/60 transition-all hover:shadow-lg hover:shadow-purple-500/20"
          style={{
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
          }}
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl sm:text-4xl">🏆</div>
            <div>
              <div className="text-xs text-gray-400 font-rajdhani uppercase tracking-wide">{t.calendar.longestStreak}</div>
              <div className="text-2xl sm:text-3xl font-black font-orbitron text-system-purple">{profile.longestStreak} {t.calendar.days}</div>
            </div>
          </div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 hover:border-blue-500/60 transition-all hover:shadow-lg hover:shadow-blue-500/20"
          style={{
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
          }}
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl sm:text-4xl">📊</div>
            <div>
              <div className="text-xs text-gray-400 font-rajdhani uppercase tracking-wide">{t.calendar.thisMonth}</div>
              <div className="text-2xl sm:text-3xl font-black font-orbitron text-system-blue">
                {calendarData.filter(d => d.isCurrentMonth && d.activity && d.activity.questsCompleted > 0).length} {t.calendar.days}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Views */}
      {view === 'week' && (
        <WeekView
          currentDate={currentDate}
          quests={quests}
          programs={programs}
          onQuestClick={handleQuestClick}
          onTimeSlotClick={handleTimeSlotClick}
          onQuestDragEnd={handleQuestDragEnd}
          onQuestResize={handleQuestResize}
        />
      )}

      {view === 'day' && selectedDay && (
        <>
          <div className="flex items-center gap-2 sm:gap-4 mb-4 flex-wrap">
            <button
              onClick={() => {
                const newDate = new Date(selectedDay);
                newDate.setDate(newDate.getDate() - 1);
                setSelectedDay(newDate.toISOString().split('T')[0]);
              }}
              className="px-3 sm:px-4 py-2 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 hover:border-slate-500/60 rounded-2xl font-orbitron font-bold transition-all text-xs sm:text-sm"
              style={{
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              ← {t.calendar.prev}
            </button>
            <button
              onClick={() => {
                const newDate = new Date(selectedDay);
                newDate.setDate(newDate.getDate() + 1);
                setSelectedDay(newDate.toISOString().split('T')[0]);
              }}
              className="px-3 sm:px-4 py-2 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 hover:border-slate-500/60 rounded-2xl font-orbitron font-bold transition-all text-xs sm:text-sm"
              style={{
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              {t.calendar.next} →
            </button>
            <button
              onClick={() => setView('month')}
              className="ml-auto px-3 sm:px-4 py-2 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 hover:border-blue-500/60 rounded-2xl font-orbitron font-bold transition-all text-xs sm:text-sm"
              style={{
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              {t.calendar.backToMonth}
            </button>
          </div>
          <DayView
            selectedDate={new Date(selectedDay)}
            quests={quests}
            programs={programs}
            onQuestClick={handleQuestClick}
            onTimeSlotClick={handleTimeSlotClick}
            onQuestDragEnd={handleQuestDragEndDay}
            onQuestResize={handleQuestResize}
          />
        </>
      )}


      {/* Month View Calendar */}
      {view === 'month' && (
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigateMonth('prev')}
            className="px-3 sm:px-4 py-2 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 hover:border-slate-500/60 rounded-2xl font-orbitron font-bold transition-all text-xs sm:text-sm"
            style={{
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            ← {t.calendar.prev}
          </button>
          <h3 className="text-sm sm:text-xl font-bold font-orbitron text-system-blue uppercase">
            {monthName}
          </h3>
          <button
            onClick={() => navigateMonth('next')}
            className="px-3 sm:px-4 py-2 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 hover:border-slate-500/60 rounded-2xl font-orbitron font-bold transition-all text-xs sm:text-sm"
            style={{
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            {t.calendar.next} →
          </button>
        </div>

        {/* Week Day Headers */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
          {weekDays.map(day => (
            <div key={day} className="text-center text-[10px] sm:text-xs font-orbitron font-bold text-gray-400 uppercase py-1 sm:py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {calendarData.map((dayData, index) => {
            const intensity = getIntensityLevel(dayData.activity);
            const isSelected = selectedDay === dayData.date;
            const isToday = dayData.date === new Date().toISOString().split('T')[0];
            const hasScheduledQuests = dayData.completedQuests.some(q => q.scheduledFor && q.status !== 'completed');
            const hasCompletedQuests = dayData.activity && dayData.activity.questsCompleted > 0;

            return (
              <button
                key={index}
                onClick={() => {
                  if (dayData.isCurrentMonth) {
                    setSelectedDay(dayData.date);
                    setView('day');
                  }
                }}
                disabled={!dayData.isCurrentMonth}
                className={`
                  aspect-square relative border-2 rounded-lg transition-all
                  ${dayData.isCurrentMonth ? 'cursor-pointer hover:scale-105 sm:hover:scale-110' : 'opacity-30 cursor-default'}
                  ${getIntensityColor(intensity)}
                  ${isSelected ? 'ring-2 sm:ring-4 ring-system-blue scale-105 shadow-lg shadow-blue-500/50' : ''}
                  ${isToday ? 'ring-4 ring-system-purple ring-offset-2 ring-offset-black/50 shadow-lg shadow-purple-500/50 scale-105' : ''}
                `}
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  {isToday && (
                    <div className="absolute top-0.5 left-1/2 -translate-x-1/2 px-1 py-0.5 bg-system-purple rounded text-[6px] sm:text-[8px] font-orbitron font-bold text-white shadow-lg shadow-purple-500/50">
                      TODAY
                    </div>
                  )}
                  <div className={`text-[10px] sm:text-sm font-bold font-orbitron ${
                    isToday ? 'text-system-purple text-lg sm:text-2xl' :
                    intensity > 2 ? 'text-white' : 'text-gray-400'
                  }`}>
                    {dayData.day}
                  </div>
                  {hasCompletedQuests && (
                    <div className="text-[8px] sm:text-xs text-system-blue font-bold">
                      {dayData.activity?.questsCompleted || 0}
                    </div>
                  )}
                </div>
                {/* Scheduled quest indicator */}
                {hasScheduledQuests && (
                  <div className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-system-purple animate-pulse shadow-neon-purple" title="Has scheduled quests"></div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 sm:mt-6 flex items-center justify-center gap-2 sm:gap-4">
          <span className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase">{t.calendar.less}</span>
          {[0, 1, 2, 3, 4].map(level => (
            <div
              key={level}
              className={`w-5 h-5 sm:w-8 sm:h-8 rounded border sm:border-2 ${getIntensityColor(level)}`}
            />
          ))}
          <span className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase">{t.calendar.more}</span>
        </div>
        </div>
      )}


      {/* Quest Edit Modal */}
      {selectedQuest && (
        <QuestEditModal
          quest={selectedQuest}
          isOpen={!!selectedQuest}
          onClose={() => setSelectedQuest(null)}
          calendarCompact
          onUpdate={(questId, updates) => {
            updateQuest(questId, updates, true); // silent=true to avoid notification spam
            setSelectedQuest(null);
          }}
          onDelete={(questId) => {
            deleteQuest(questId);
            setSelectedQuest(null);
          }}
          onComplete={(questId) => {
            completeQuest(questId);
            setSelectedQuest(null);
          }}
        />
      )}

      {showCreateModal && (
        <CreateQuestModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={(title, description, type, difficulty, resetDaily, scheduledFor, subTasks, scheduledTime, allDay) => {
            createQuest(
              title,
              description,
              type,
              difficulty,
              resetDaily,
              scheduledFor || prefilledDate,
              subTasks,
              scheduledTime || (prefilledTime ? { start: prefilledTime, end: (() => {
                const [h, m] = prefilledTime.split(':').map(Number);
                const endMinutes = h * 60 + m + 60;
                const eh = String(Math.floor(endMinutes / 60)).padStart(2, '0');
                const em = String(endMinutes % 60).padStart(2, '0');
                return `${eh}:${em}`;
              })() } : undefined),
              allDay
            );
            setShowCreateModal(false);
            setPrefilledDate(undefined);
            setPrefilledTime(undefined);
          }}
          prefilledDate={prefilledDate}
          prefilledTime={prefilledTime}
        />
      )}
    </div>
  );
};

export default CalendarPage;

